import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Icon,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import {
  CompanyUsersTab,
  CurrentUsageTab,
  ExportUsersButton,
  UsageHistoryDetailTab,
  UsageHistoryTab,
} from 'components/CompanyDetail';
import { PageContainer, PageHeader } from 'components/ds';
import { getUserInfoStorage } from 'helpers/lib';
import { Plus } from 'lucide-react';
import {
  useOrganizationUsageCurrent,
  useOrganizationUsageHistoryCurrent,
} from 'queries/useOrganization';
import { useListUsers, useUpdateUser } from 'queries/useUser';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * "Usage & members" — full PAGE for the admin's own organization (mockup IA:
 * Administration › Usage & members). Mirrors the org-detail tabs but rendered
 * inline as a DS page (not the legacy modal). Data/wiring reuse the same hooks.
 */
const UsageMembers = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [isEditingUser, setIsEditingUser] = useState(false);

  const { data: users, isLoading: isLoadingUsers } = useListUsers();
  const { data: usageData, isLoading: isLoadingUsage } = useOrganizationUsageCurrent();
  const { data: usageHistoryData, isLoading: isLoadingHistory } =
    useOrganizationUsageHistoryCurrent();

  const updateUserMutation = useUpdateUser();
  const handleUpdateUser = async (userId: string, userData: any) => {
    const currentUser = users?.find((u: any) => u.id === userId);
    if (!currentUser) throw new Error('User not found');
    const result = await updateUserMutation.mutateAsync({
      userId,
      userData: {
        first_name: userData.first_name,
        last_name: userData.last_name,
        role: userData.role,
        email: currentUser.email,
      },
    });
    if (!result || !result.id) throw new Error('User update failed: Invalid response');
    // useUpdateUser already invalidates ['users'] on success — no manual invalidate needed.
  };

  const companyName =
    usageData?.name ||
    (getUserInfoStorage()?.tokenParsed?.org as string) ||
    t('route.usageMembers');

  // Header meta (mockup): "N accounts · expires <date>".
  const expiry =
    usageData?.limitation?.expiration && usageData.limitation.expiration !== 'Unlimited'
      ? new Date(usageData.limitation.expiration).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : null;
  const subtitle =
    [
      typeof usageData?.user_count === 'number'
        ? t('managementSystem.companyDetail.accountsCount', { count: usageData.user_count })
        : null,
      expiry ? `${t('managementSystem.companyDetail.expirationDate')}: ${expiry}` : null,
    ]
      .filter(Boolean)
      .join(' · ') || undefined;

  const TAB_LABELS = [
    'managementSystem.companyDetail.currentUsage',
    'managementSystem.companyDetail.usageHistory',
    'managementSystem.companyDetail.usageHistoryDetail',
    'managementSystem.companyDetail.companyUsers',
  ];

  if (isLoadingUsage || isLoadingHistory || isLoadingUsers) {
    return (
      <PageContainer>
        <PageHeader title={t('route.usageMembers')} />
        <Center py={20}>
          <Spinner color='primary.solid' size='xl' />
        </Center>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={companyName}
        subtitle={subtitle}
        action={
          <Badge display='flex' variant='ds-success'>
            <HStack spacing={1}>
              <Box w='6px' h='6px' bg='state.success' borderRadius='full' />
              <Text>{t('managementSystem.companyDetail.activeStatus')}</Text>
            </HStack>
          </Badge>
        }
      />

      {/* Tabs sit directly on the page canvas (mockup) — no outer card border/bg. */}
      <Tabs index={activeTab} onChange={setActiveTab} variant='unstyled'>
        <TabList gap={1} mb={5} borderColor='border.default' borderBottomWidth='1px'>
          {TAB_LABELS.map((key) => (
            <Tab
              key={key}
              mb='-1px'
              px={3}
              py={3}
              color='text.muted'
              fontSize='sm'
              fontWeight={500}
              borderColor='transparent'
              borderBottomWidth='2px'
              _selected={{ color: 'primary.solid', borderColor: 'primary.solid' }}
            >
              {t(key)}
            </Tab>
          ))}
        </TabList>

        {activeTab === 3 && (
          <Flex align='center' justify='space-between' pb={4}>
            <Text color='text.muted' fontSize='sm' fontWeight={500}>
              {t('managementSystem.companyDetail.membersCount', {
                count: users?.length || 0,
                defaultValue: '{{count}} members',
              })}
            </Text>
            <HStack spacing={2}>
              <ExportUsersButton isDisabled={isEditingUser} />
              <Button
                isDisabled={isEditingUser}
                leftIcon={<Icon as={Plus} boxSize='14px' />}
                onClick={() => window.dispatchEvent(new CustomEvent('triggerAddUser'))}
                size='ds-sm'
                variant='ds-primary'
              >
                {t('managementSystem.companyDetail.addUser')}
              </Button>
            </HStack>
          </Flex>
        )}

        <TabPanels>
          <TabPanel px={0} pt={0} pb={4}>
            <CurrentUsageTab
              usageData={usageData}
              limitation={usageData?.limitation}
              showTokenUsage={false}
            />
          </TabPanel>
          <TabPanel px={0} pt={0} pb={4}>
            <UsageHistoryTab usageData={usageHistoryData} />
          </TabPanel>
          <TabPanel px={0} pt={0} pb={4}>
            <UsageHistoryDetailTab usageData={usageHistoryData} />
          </TabPanel>
          <TabPanel px={0} pt={0} pb={4}>
            <CompanyUsersTab
              users={users}
              organizationId=''
              onUpdateUser={handleUpdateUser}
              onEditingStateChange={setIsEditingUser}
              isNotSuperAdmin={true}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </PageContainer>
  );
};

export default UsageMembers;
